import { handleActions } from 'redux-actions';

import { logoutSuccess } from 'ducks/auth/actions';
import { ENTITY_STATUS } from 'app/types/constants';

import {
  routesRequest,
  routesSuccess,
  routesFailure,
  detailRequest,
  detailSuccess,
  detailFailure,
  createRouteRequest,
  createRouteSuccess,
  createRouteFailure,
  updateRouteRequest,
  updateRouteSuccess,
  updateRouteFailure,
  copyRouteRequest,
  copyRouteSuccess,
  copyRouteFailure,
  suspendRouteRequest,
  suspendRouteSuccess,
  suspendRouteFailure,
  resumeRouteRequest,
  resumeRouteSuccess,
  resumeRouteFailure,
  deleteRouteRequest,
  deleteRouteSuccess,
  deleteRouteFailure,
  runsRequest,
  runsSuccess,
  runsFailure,
  runDetailRequest,
  runDetailSuccess,
  runDetailFailure,
  runsListRequest,
  runsListSuccess,
  runsListFailure,
  runByIdRequest,
  runByIdSuccess,
  runByIdFailure,
} from './actions';
import initialState from './states';

const setLoading = (state, op, value) => ({
  ...state,
  loading: { ...state.loading, [op]: value },
});

const setError = (state, op, value) => ({
  ...state,
  errors: { ...state.errors, [op]: value },
  loading: { ...state.loading, [op]: false },
});

const clearError = (state, op, extra) => ({
  ...state,
  errors: { ...state.errors, [op]: null },
  loading: { ...state.loading, [op]: false },
  ...extra,
});

const patchListItemStatus = (state, mobileRouteID, status) => ({
  ...state,
  list: {
    ...state.list,
    rows: state.list.rows.map((row) =>
      row.mobileRouteID === mobileRouteID ? { ...row, status } : row
    ),
  },
});

export default handleActions(
  {
    /** ----- List ----- */
    [routesRequest](state) {
      return setLoading(state, 'list', true);
    },
    [routesSuccess](state, { payload }) {
      const {
        rows = [],
        totalCount = 0,
        page,
        pageSize,
        sortBy,
        sortDir,
        search,
        status,
        mobilePatrolID,
      } = payload || {};
      return {
        ...state,
        list: {
          ...state.list,
          rows,
          totalCount,
          page: page ?? state.list.page,
          pageSize: pageSize ?? state.list.pageSize,
          sortBy: sortBy ?? null,
          sortDir: sortDir ?? null,
          search: search ?? '',
          status: status ?? null,
          mobilePatrolID: mobilePatrolID ?? null,
        },
        loading: { ...state.loading, list: false },
        errors: { ...state.errors, list: null },
      };
    },
    [routesFailure](state, { payload }) {
      return setError(state, 'list', payload);
    },

    /** ----- Detail ----- */
    [detailRequest](state) {
      return {
        ...setLoading(state, 'detail', true),
        selected: null,
        errors: { ...state.errors, detail: null },
      };
    },
    [detailSuccess](state, { payload }) {
      return clearError(state, 'detail', { selected: payload });
    },
    [detailFailure](state, { payload }) {
      return setError(state, 'detail', payload);
    },

    /** ----- Create ----- */
    [createRouteRequest](state) {
      return setLoading(state, 'create', true);
    },
    [createRouteSuccess](state) {
      return clearError(state, 'create');
    },
    [createRouteFailure](state, { payload }) {
      return setError(state, 'create', payload);
    },

    /** ----- Update ----- */
    [updateRouteRequest](state) {
      return setLoading(state, 'update', true);
    },
    [updateRouteSuccess](state, { payload }) {
      const selected =
        payload && state.selected && payload.mobileRouteID === state.selected.mobileRouteID
          ? payload
          : state.selected;
      return clearError(state, 'update', { selected });
    },
    [updateRouteFailure](state, { payload }) {
      return setError(state, 'update', payload);
    },

    /** ----- Copy ----- */
    [copyRouteRequest](state) {
      return setLoading(state, 'copy', true);
    },
    [copyRouteSuccess](state) {
      return clearError(state, 'copy');
    },
    [copyRouteFailure](state, { payload }) {
      return setError(state, 'copy', payload);
    },

    /** ----- Suspend ----- */
    [suspendRouteRequest](state) {
      return setLoading(state, 'suspend', true);
    },
    [suspendRouteSuccess](state, { payload: { mobileRouteID } }) {
      const next = patchListItemStatus(state, mobileRouteID, ENTITY_STATUS.SUSPENDED);
      return {
        ...next,
        loading: { ...next.loading, suspend: false },
        errors: { ...next.errors, suspend: null },
        selected:
          next.selected && next.selected.mobileRouteID === mobileRouteID
            ? { ...next.selected, status: ENTITY_STATUS.SUSPENDED }
            : next.selected,
      };
    },
    [suspendRouteFailure](state, { payload }) {
      return setError(state, 'suspend', payload);
    },

    /** ----- Resume ----- */
    [resumeRouteRequest](state) {
      return setLoading(state, 'resume', true);
    },
    [resumeRouteSuccess](state, { payload: { mobileRouteID } }) {
      const next = patchListItemStatus(state, mobileRouteID, ENTITY_STATUS.ACTIVE);
      return {
        ...next,
        loading: { ...next.loading, resume: false },
        errors: { ...next.errors, resume: null },
        selected:
          next.selected && next.selected.mobileRouteID === mobileRouteID
            ? { ...next.selected, status: ENTITY_STATUS.ACTIVE }
            : next.selected,
      };
    },
    [resumeRouteFailure](state, { payload }) {
      return setError(state, 'resume', payload);
    },

    /** ----- Delete ----- */
    [deleteRouteRequest](state) {
      return setLoading(state, 'delete', true);
    },
    [deleteRouteSuccess](state, { payload: { mobileRouteID } }) {
      const rows = state.list.rows.filter((row) => row.mobileRouteID !== mobileRouteID);
      const removed = rows.length < state.list.rows.length;
      return {
        ...state,
        list: {
          ...state.list,
          rows,
          totalCount: removed ? Math.max(0, state.list.totalCount - 1) : state.list.totalCount,
        },
        loading: { ...state.loading, delete: false },
        errors: { ...state.errors, delete: null },
      };
    },
    [deleteRouteFailure](state, { payload }) {
      return setError(state, 'delete', payload);
    },

    /** ----- Run history ----- */
    [runsRequest](state) {
      return setLoading(state, 'runs', true);
    },
    [runsSuccess](state, { payload }) {
      const { mobileRouteID, rows = [], totalCount = 0, page, pageSize } = payload || {};
      const prevRows = state.runHistory?.[mobileRouteID]?.rows || [];
      const nextRows = (page ?? 1) > 1 ? [...prevRows, ...rows] : rows;
      return {
        ...state,
        runHistory: {
          ...state.runHistory,
          [mobileRouteID]: {
            rows: nextRows,
            totalCount,
            page: page ?? 1,
            pageSize: pageSize ?? 25,
          },
        },
        loading: { ...state.loading, runs: false },
        errors: { ...state.errors, runs: null },
      };
    },
    [runsFailure](state, { payload }) {
      return setError(state, 'runs', payload);
    },

    /** ----- Run detail ----- */
    [runDetailRequest](state) {
      return { ...setLoading(state, 'runDetail', true), selectedRun: null };
    },
    [runDetailSuccess](state, { payload }) {
      return clearError(state, 'runDetail', { selectedRun: payload });
    },
    [runDetailFailure](state, { payload }) {
      return setError(state, 'runDetail', payload);
    },

    /** ----- All runs (Operations "Mobile Route Runs" page) ----- */
    [runsListRequest](state) {
      return setLoading(state, 'runsList', true);
    },
    [runsListSuccess](state, { payload }) {
      const { rows = [], totalCount = 0, page, pageSize, counts = null } = payload || {};
      const prevRows = state.runsList.rows || [];
      const nextRows = (page ?? 1) > 1 ? [...prevRows, ...rows] : rows;
      return {
        ...state,
        runsList: {
          ...state.runsList,
          rows: nextRows,
          totalCount,
          page: page ?? state.runsList.page,
          pageSize: pageSize ?? state.runsList.pageSize,
          counts: counts ?? state.runsList.counts,
        },
        loading: { ...state.loading, runsList: false },
        errors: { ...state.errors, runsList: null },
      };
    },
    [runsListFailure](state, { payload }) {
      return setError(state, 'runsList', payload);
    },

    /** ----- Run detail by run id (route-context-free) ----- */
    [runByIdRequest](state) {
      // Clear any prior run-by-id error so a stale failure can't fire the not-found redirect
      // on a subsequent valid run before this request resolves.
      return {
        ...setLoading(state, 'runById', true),
        selectedRun: null,
        errors: { ...state.errors, runById: null },
      };
    },
    [runByIdSuccess](state, { payload }) {
      return clearError(state, 'runById', { selectedRun: payload });
    },
    [runByIdFailure](state, { payload }) {
      return setError(state, 'runById', payload);
    },

    [logoutSuccess]() {
      return initialState;
    },
  },
  initialState
);
